import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getKitchenUser,
  getKitchenOrders,
  updateKitchenOrderStatus,
  kitchenLogout,
} from "./kitchenApi";
import KitchenOrderCard from "./KitchenOrderCard";
import useNewOrderSound from "../components/useNewOrderSound";
import "./kitchen.css";

const REFRESH_INTERVAL = 5000;

export default function KitchenDashboard() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const busyRef = useRef(false);
  const { soundEnabled, enableSound, disableSound, observeOrders } = useNewOrderSound(restaurantId);

  const refreshOrders = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const data = await getKitchenOrders(restaurantId);
      const nextOrders = data.orders || [];
      // Notify only when an approved order first enters the kitchen's new-order queue.
      observeOrders(nextOrders.filter((order) =>
        ["PENDING", "CONFIRMED"].includes(order.status)
      ));
      setOrders(nextOrders);
      setLastUpdated(new Date());
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  }, [restaurantId, observeOrders]);

  useEffect(() => {
    let active = true;
    async function initialize() {
      try {
        const currentUser = await getKitchenUser(restaurantId);
        if (!active) return;
        setUser(currentUser);
        await refreshOrders();
      } catch (err) {
        if (active) {
          setError(err.message);
          setLoading(false);
        }
      }
    }
    initialize();
    const interval = setInterval(() => {
      if (active) refreshOrders();
    }, REFRESH_INTERVAL);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [restaurantId, refreshOrders]);

  async function handleUpdate(orderId, newStatus) {
    if (updatingId !== null) return;
    setUpdatingId(orderId);
    setError("");
    try {
      await updateKitchenOrderStatus(restaurantId, orderId, newStatus);
      await refreshOrders();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleLogout() {
    try {
      await kitchenLogout(restaurantId);
      navigate(`/kitchen/${restaurantId}`, { replace: true });
    } catch (err) {
      setError(err.message);
    }
  }

  const newOrders = orders.filter((order) =>
    ["PENDING", "CONFIRMED"].includes(order.status)
  );
  const preparingOrders = orders.filter((order) => order.status === "PREPARING");
  const readyOrders = orders.filter((order) => order.status === "READY");

  return (
    <div className="kitchen-dashboard">
      <header className="kitchen-dashboard-header">
        <div>
          <h1>👨‍🍳 Kitchen Display</h1>
          <p>{user?.restaurant?.name || `Restaurant #${restaurantId}`}</p>
        </div>
        <div className="kitchen-header-actions">
          <button type="button" onClick={() => soundEnabled ? disableSound() : enableSound().catch(err => setError(err.message))}>
            {soundEnabled ? "🔊 Sound On" : "🔇 Enable Sound"}
          </button>
          <button onClick={refreshOrders}>Refresh</button>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </header>

      {error && <div className="kitchen-dashboard-error" role="alert">{error}</div>}

      <section className="kitchen-summary">
        <div className="kitchen-summary-box"><span>New Orders</span><strong>{newOrders.length}</strong></div>
        <div className="kitchen-summary-box"><span>Preparing</span><strong>{preparingOrders.length}</strong></div>
        <div className="kitchen-summary-box"><span>Ready</span><strong>{readyOrders.length}</strong></div>
      </section>

      {lastUpdated && <p className="kitchen-last-updated">Last updated: {lastUpdated.toLocaleTimeString()}</p>}

      {loading ? (
        <div className="kitchen-empty">Loading approved orders...</div>
      ) : (
        <div className="kitchen-columns">
          {[
            { title: "New Orders", list: newOrders, type: "new" },
            { title: "Preparing", list: preparingOrders, type: "preparing" },
            { title: "Ready", list: readyOrders, type: "ready" },
          ].map((column) => (
            <section className="kitchen-column" key={column.type}>
              <h2>{column.title}<span>{column.list.length}</span></h2>
              {column.list.length === 0 ? (
                <div className="kitchen-empty">No orders</div>
              ) : (
                column.list.map((order) => (
                  <KitchenOrderCard
                    key={order.id}
                    order={order}
                    updating={updatingId === order.id}
                    onUpdate={handleUpdate}
                  />
                ))
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}